// ShiftWidget — cashier shift open/close with cash handover (NON-blocking:
// selling works without an open shift; payments made while one is open are
// tagged server-side). Floating chip (bottom-right, left of the AI button) →
// panel with open / live status / close + handover. Mounted app-wide in Layout
// for seller roles.
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../config/api';
import useAuthStore from '../store/authStore';
import { useFeatures } from '../hooks/useFeatures';

const GREEN = '#2e9f86';
const fmtUSD = (n) => `$ ${(Number(n) || 0).toFixed(2)}`;
const fmtLBP = (n) => `LBP ${Math.round(Number(n) || 0).toLocaleString()}`;
const time = (d) => d ? new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

const inputCls = 'w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500';

function CashInputs({ label, usd, lbp, setUsd, setLbp, t }) {
  return (
    <div>
      <div className="text-xs font-semibold text-gray-500 mb-1">{label}</div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <input type="number" min="0" step="0.01" className={inputCls} placeholder="USD"
            value={usd} onChange={(e) => setUsd(e.target.value)} />
          <div className="text-[10px] text-gray-400 mt-0.5">USD $</div>
        </div>
        <div>
          <input type="number" min="0" step="1000" className={inputCls} placeholder="LBP"
            value={lbp} onChange={(e) => setLbp(e.target.value)} />
          <div className="text-[10px] text-gray-400 mt-0.5">LBP</div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, usd, lbp, bold, color }) {
  return (
    <div className={`flex items-center justify-between py-1 text-sm ${bold ? 'font-bold' : ''}`} style={color ? { color } : undefined}>
      <span className="text-gray-600" style={color ? { color } : undefined}>{label}</span>
      <span className="text-end">{fmtUSD(usd)}<span className="text-gray-300 mx-1">·</span>{fmtLBP(lbp)}</span>
    </div>
  );
}

function printZReport(shift, t) {
  const d = shift.difference || { usd: 0, lbp: 0 };
  const rows = (label, o) => `<tr><td>${label}</td><td style="text-align:right">${fmtUSD(o?.usd)}</td><td style="text-align:right">${fmtLBP(o?.lbp)}</td></tr>`;
  const methods = Object.entries(shift.totals?.byMethod || {})
    .map(([k, v]) => `<tr><td>${k} (${v.count})</td><td style="text-align:right">${fmtUSD(v.usd)}</td><td style="text-align:right">${fmtLBP(v.lbp)}</td></tr>`).join('');
  const html = `<html><head><title>Z-Report</title><style>
    body{font-family:Arial,sans-serif;font-size:13px;padding:24px;max-width:480px;margin:auto}
    h2{margin:0 0 2px} .sub{color:#666;margin-bottom:14px}
    table{width:100%;border-collapse:collapse;margin-bottom:14px}
    td,th{padding:5px 4px;border-bottom:1px solid #e5e7eb} th{text-align:left;background:#f3f4f6}
    .tot td{font-weight:bold;border-top:2px solid #111}
  </style></head><body>
    <h2>${t('shift.zreport', 'Shift Z-Report')}</h2>
    <div class="sub">${shift.user_name || ''} · ${new Date(shift.opened_at).toLocaleString()} → ${shift.closed_at ? new Date(shift.closed_at).toLocaleString() : '—'}</div>
    <table>
      <tr><th>${t('shift.cash', 'Cash')}</th><th style="text-align:right">USD</th><th style="text-align:right">LBP</th></tr>
      ${rows(t('shift.opening', 'Opening cash'), shift.opening_cash)}
      ${rows(t('shift.cashIn', 'Cash received'), shift.totals?.cashIn)}
      ${rows(t('shift.cashOut', 'Cash expenses'), shift.totals?.cashOut)}
      ${rows(t('shift.expected', 'Expected'), shift.expected_cash || shift.expected)}
      ${rows(t('shift.counted', 'Counted'), shift.counted_cash)}
      <tr class="tot"><td>${t('shift.difference', 'Difference')}</td><td style="text-align:right">${fmtUSD(d.usd)}</td><td style="text-align:right">${fmtLBP(d.lbp)}</td></tr>
    </table>
    ${methods ? `<table><tr><th>${t('shift.byMethod', 'Payments by method')}</th><th style="text-align:right">USD</th><th style="text-align:right">LBP</th></tr>${methods}</table>` : ''}
    ${shift.handover_to_name ? `<div>${t('shift.handedTo', 'Handed over to')}: <b>${shift.handover_to_name}</b></div>` : ''}
  </body></html>`;
  const w = window.open('', '_blank', 'width=560,height=720');
  if (!w) return;
  w.document.write(html); w.document.close();
  setTimeout(() => { w.focus(); w.print(); }, 250);
}

// inline=true → chip renders in normal document flow (POS header);
// inline=false → floating chip bottom-right (mounted app-wide in Layout,
// which skips the POS page where the inline one is shown instead).
export default function ShiftWidget({ inline = false }) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const features = useFeatures();
  const onPos = useLocation().pathname.startsWith('/pos');
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);        // panel visible
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [closed, setClosed] = useState(null);     // just-closed shift summary
  // open form
  const [openUsd, setOpenUsd] = useState('');
  const [openLbp, setOpenLbp] = useState('');
  // close form
  const [cntUsd, setCntUsd] = useState('');
  const [cntLbp, setCntLbp] = useState('');
  const [handTo, setHandTo] = useState('');
  const [notes, setNotes] = useState('');

  const seller = ['admin', 'manager', 'cashier'].includes(user?.role);

  const { data: shift } = useQuery(['shift-current'],
    () => api.get('/api/shifts/current').then((r) => r.data),
    { enabled: seller, refetchInterval: 60000 });

  // exchange rate — with the "second currency" feature on, payments are recorded
  // in the actual tendered currency, so the USD/LBP drawer split is accurate;
  // the rate is only used for the convenience "Net (USD equivalent)" line.
  const { data: settings } = useQuery(['settings'],
    () => api.get('/api/settings').then((r) => r.data), { enabled: seller });
  const rate = Number(settings?.exchange_rate) || 89500;

  const { data: pending } = useQuery(['shift-pending-handover'],
    () => api.get('/api/shifts/pending-handover').then((r) => r.data),
    { enabled: seller && open && !shift });

  const { data: sellers = [] } = useQuery(['shift-sellers'],
    () => api.get('/api/shifts/sellers').then((r) => r.data),
    { enabled: seller && open && !!shift });

  // prefill opening count from a pending handover (incoming cashier confirms it)
  React.useEffect(() => {
    if (pending && openUsd === '' && openLbp === '') {
      setOpenUsd(String(pending.counted_cash?.usd ?? ''));
      setOpenLbp(String(pending.counted_cash?.lbp ?? ''));
    }
  }, [pending]); // eslint-disable-line react-hooks/exhaustive-deps

  // feature flag (Settings → Features → Cashier shifts; default ON)
  if (features.shifts === false) return null;
  if (!seller) return null;
  // floating widget hides on POS — the POS header shows the inline one
  if (!inline && onPos) return null;

  const refresh = () => {
    queryClient.invalidateQueries(['shift-current']);
    queryClient.invalidateQueries(['shift-pending-handover']);
  };

  const doOpen = async () => {
    setBusy(true); setError('');
    try {
      await api.post('/api/shifts/open', {
        opening_cash: { usd: Number(openUsd) || 0, lbp: Number(openLbp) || 0 },
        from_shift_id: pending?._id || undefined,
      });
      setOpenUsd(''); setOpenLbp(''); refresh();
    } catch (e) { setError(e.response?.data?.error || e.message); }
    setBusy(false);
  };

  const doClose = async () => {
    setBusy(true); setError('');
    try {
      const sel = sellers.find((s) => String(s._id) === handTo);
      const { data } = await api.post('/api/shifts/close', {
        counted_cash: { usd: Number(cntUsd) || 0, lbp: Number(cntLbp) || 0 },
        handover_to_id: handTo || undefined,
        handover_to_name: sel ? sel.name : undefined,
        notes: notes || undefined,
      });
      setClosed(data);
      setCntUsd(''); setCntLbp(''); setHandTo(''); setNotes(''); refresh();
    } catch (e) { setError(e.response?.data?.error || e.message); }
    setBusy(false);
  };

  const exp = shift?.expected || { usd: 0, lbp: 0 };
  const liveDiff = {
    usd: (Number(cntUsd) || 0) - exp.usd,
    lbp: (Number(cntLbp) || 0) - exp.lbp,
  };
  const diffColor = (v) => (Math.abs(v) < 0.01 ? '#16a34a' : v < 0 ? '#dc2626' : '#d97706');

  return (
    <>
      {/* chip */}
      <button onClick={() => { setOpen(o => !o); setClosed(null); setError(''); }}
        className={inline
          ? 'shrink-0 rounded-full px-3 py-1 text-xs font-semibold flex items-center gap-1.5 shadow-sm'
          : 'fixed z-[996] shadow-lg rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2'}
        style={{
          ...(inline ? {} : { bottom: 24, insetInlineEnd: 88 }),
          background: shift ? GREEN : '#fff',
          color: shift ? '#fff' : '#374151',
          border: shift ? 'none' : '1px solid #d1d5db',
        }}
        title={t('shift.title', 'Cashier shift')}>
        <span>⏱</span>
        {shift
          ? <span>{t('shift.openSince', 'Shift')} · {time(shift.opened_at)}</span>
          : <span>{t('shift.openBtn', 'Open shift')}</span>}
      </button>

      {/* panel */}
      {open && (
        <div className="fixed z-[996] bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 w-[340px] max-w-[calc(100vw-32px)] max-h-[75vh] overflow-auto"
          style={inline ? { top: 96, insetInlineEnd: 24 } : { bottom: 76, insetInlineEnd: 24 }}>
          <div className="flex items-center justify-between mb-3">
            <div className="font-bold text-gray-800">{t('shift.title', 'Cashier shift')}</div>
            <button className="text-gray-400 hover:text-gray-600" onClick={() => setOpen(false)}>✕</button>
          </div>

          {error && <div className="mb-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2">{error}</div>}

          {/* just closed → summary */}
          {closed ? (
            <div>
              <div className="text-sm font-semibold text-gray-700 mb-2">{t('shift.closedOk', 'Shift closed')} ✓</div>
              <Row label={t('shift.expected', 'Expected')} usd={closed.expected_cash?.usd} lbp={closed.expected_cash?.lbp} />
              <Row label={t('shift.counted', 'Counted')} usd={closed.counted_cash?.usd} lbp={closed.counted_cash?.lbp} />
              <Row label={t('shift.difference', 'Difference')} bold
                usd={closed.difference?.usd} lbp={closed.difference?.lbp}
                color={diffColor((closed.difference?.usd || 0) + (closed.difference?.lbp || 0) / rate)} />
              <div className="text-[11px] text-end font-semibold" style={{ color: diffColor((closed.difference?.usd || 0) + (closed.difference?.lbp || 0) / rate) }}>
                {t('shift.netDiff', 'Net (USD equiv.)')}: {fmtUSD((closed.difference?.usd || 0) + (closed.difference?.lbp || 0) / rate)}
              </div>
              {closed.handover_to_name && (
                <div className="text-xs text-gray-500 mt-1">{t('shift.handedTo', 'Handed over to')}: <b>{closed.handover_to_name}</b></div>
              )}
              <div className="flex gap-2 mt-3">
                <button className="flex-1 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: GREEN }}
                  onClick={() => printZReport({ ...closed, totals: closed.totals }, t)}>
                  🖨 {t('shift.printZ', 'Print Z-report')}
                </button>
                <button className="flex-1 py-2 rounded-lg text-sm font-semibold border" onClick={() => { setClosed(null); setOpen(false); }}>
                  {t('shift.close', 'Close')}
                </button>
              </div>
            </div>

          ) : !shift ? (
            /* ── no open shift → open form ── */
            <div className="space-y-3">
              {pending && (
                <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-2">
                  {t('shift.pendingFrom', 'Drawer handed over from')} <b>{pending.user_name}</b> ({time(pending.closed_at)}):{' '}
                  {fmtUSD(pending.counted_cash?.usd)} · {fmtLBP(pending.counted_cash?.lbp)}.{' '}
                  {t('shift.confirmCount', 'Count it and confirm below.')}
                </div>
              )}
              <CashInputs label={t('shift.openingCount', 'Opening cash count')} t={t}
                usd={openUsd} lbp={openLbp} setUsd={setOpenUsd} setLbp={setOpenLbp} />
              <button disabled={busy} onClick={doOpen}
                className="w-full py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50" style={{ background: GREEN }}>
                {busy ? '…' : t('shift.openBtn', 'Open shift')}
              </button>
              <div className="text-[11px] text-gray-400">{t('shift.nonBlocking', 'Selling works without a shift — opening one tracks the cash drawer.')}</div>
            </div>

          ) : (
            /* ── open shift → live status + close form ── */
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-500 mb-1">
                  {shift.user_name} · {t('shift.since', 'since')} {time(shift.opened_at)}
                </div>
                <Row label={t('shift.opening', 'Opening cash')} usd={shift.opening_cash?.usd} lbp={shift.opening_cash?.lbp} />
                <Row label={t('shift.cashIn', 'Cash received')} usd={shift.totals?.cashIn?.usd} lbp={shift.totals?.cashIn?.lbp} />
                <Row label={t('shift.cashOut', 'Cash expenses')} usd={shift.totals?.cashOut?.usd} lbp={shift.totals?.cashOut?.lbp} />
                <Row label={t('shift.expected', 'Expected in drawer')} bold usd={exp.usd} lbp={exp.lbp} />
                {shift.open_discrepancy && (Math.abs(shift.open_discrepancy.usd) > 0.009 || Math.abs(shift.open_discrepancy.lbp) > 0.9) && (
                  <Row label={t('shift.openDiff', 'Handover discrepancy')} usd={shift.open_discrepancy.usd} lbp={shift.open_discrepancy.lbp} color="#dc2626" />
                )}
              </div>

              <CashInputs label={t('shift.countedNow', 'Counted cash (to close)')} t={t}
                usd={cntUsd} lbp={cntLbp} setUsd={setCntUsd} setLbp={setCntLbp} />
              {(cntUsd !== '' || cntLbp !== '') && (
                <>
                  <Row label={t('shift.difference', 'Difference')} bold usd={liveDiff.usd} lbp={liveDiff.lbp}
                    color={diffColor(liveDiff.usd + liveDiff.lbp / rate)} />
                  <div className="text-[11px] text-end font-semibold" style={{ color: diffColor(liveDiff.usd + liveDiff.lbp / rate) }}>
                    {t('shift.netDiff', 'Net (USD equiv.)')}: {fmtUSD(liveDiff.usd + liveDiff.lbp / rate)}
                  </div>
                </>
              )}

              <div>
                <div className="text-xs font-semibold text-gray-500 mb-1">{t('shift.handoverTo', 'Hand drawer to (optional)')}</div>
                <select className={inputCls} value={handTo} onChange={(e) => setHandTo(e.target.value)}>
                  <option value="">{t('shift.noHandover', '— No handover (drop to safe) —')}</option>
                  {sellers.filter((s) => String(s._id) !== String(user?._id || user?.id)).map((s) => (
                    <option key={s._id} value={s._id}>{s.name} ({s.role})</option>
                  ))}
                </select>
              </div>
              <input className={inputCls} placeholder={t('shift.notes', 'Notes (optional)')}
                value={notes} onChange={(e) => setNotes(e.target.value)} />

              <button disabled={busy || (cntUsd === '' && cntLbp === '')} onClick={doClose}
                className="w-full py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50" style={{ background: '#dc2626' }}>
                {busy ? '…' : t('shift.closeBtn', 'Close shift')}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
